import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  FileText, Plus, Send, Clock, CheckCircle, AlertTriangle, Calendar,
  Building2, Download, Eye, Filter, MoreVertical, RefreshCw, ChevronRight,
  FileCheck, Bell, Settings, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Regulatory Authorities
const AUTHORITIES = [
  { value: 'icao', label: 'ICAO', fullName: 'International Civil Aviation Organization' },
  { value: 'gaca', label: 'GACA', fullName: 'General Authority of Civil Aviation (Saudi)' },
  { value: 'easa', label: 'EASA', fullName: 'European Union Aviation Safety Agency' },
  { value: 'faa', label: 'FAA', fullName: 'Federal Aviation Administration' },
  { value: 'custom', label: 'Custom', fullName: 'Custom/Internal' }
];

// Report Types
const REPORT_TYPES = [
  { value: 'occurrence', label: 'Occurrence Report', description: 'Safety occurrence/incident reports' },
  { value: 'mor', label: 'Mandatory Occurrence Report', description: 'MOR for regulatory compliance' },
  { value: 'annual_safety', label: 'Annual Safety Report', description: 'Yearly safety performance summary' },
  { value: 'quarterly_safety', label: 'Quarterly Safety Report', description: 'Quarterly safety metrics' },
  { value: 'spi_report', label: 'SPI Report', description: 'Safety Performance Indicators' },
  { value: 'audit_summary', label: 'Audit Summary', description: 'Audit findings summary' },
  { value: 'hazard_log', label: 'Hazard Log', description: 'Hazard register report' },
  { value: 'capa_status', label: 'CAPA Status', description: 'Corrective action status' },
  { value: 'training_compliance', label: 'Training Compliance', description: 'Training completion report' },
  { value: 'sms_effectiveness', label: 'SMS Effectiveness', description: 'SMS maturity assessment' }
];

// Report Formats
const FORMATS = [
  { value: 'pdf', label: 'PDF' },
  { value: 'xml', label: 'XML' },
  { value: 'eccairs2', label: 'ECCAIRS 2' },
  { value: 'json', label: 'JSON' },
  { value: 'csv', label: 'CSV' }
];

// Status configurations
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-500/20 text-slate-400 border-slate-500/30', icon: FileText },
  pending_review: { label: 'Pending Review', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  approved: { label: 'Approved', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  submitted: { label: 'Submitted', color: 'bg-sky-500/20 text-sky-400 border-sky-500/30', icon: Send },
  acknowledged: { label: 'Acknowledged', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: FileCheck },
  rejected: { label: 'Rejected', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertTriangle },
  resubmission_required: { label: 'Resubmission Required', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: RefreshCw }
};

const getStatusInfo = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.draft;

export default function RegulatoryReportsPage() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { api } = useAuth();
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState('reports');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [authorityFilter, setAuthorityFilter] = useState('all');
  
  // Generate form state
  const [generateForm, setGenerateForm] = useState({
    report_type: '',
    authority: '',
    format: 'pdf',
    date_from: '',
    date_to: '',
    notes: ''
  });
  
  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    report_type: '',
    authority: '',
    format: 'pdf',
    frequency: 'monthly',
    submission_method: 'manual',
    recipients: '',
    auto_submit: false,
    review_required: true,
    due_day_of_month: 15
  });
  
  // Fetch reports
  const { data: reportsData, isLoading: reportsLoading } = useQuery({
    queryKey: ['regulatory-reports', statusFilter, authorityFilter],
    queryFn: async () => {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (authorityFilter !== 'all') params.authority = authorityFilter;
      const response = await api.get('/regulatory-reports', { params });
      return response.data;
    }
  });
  
  // Fetch schedules
  const { data: schedulesData, isLoading: schedulesLoading } = useQuery({
    queryKey: ['regulatory-schedules'],
    queryFn: async () => {
      const response = await api.get('/regulatory-reports/schedules');
      return response.data;
    }
  });
  
  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['regulatory-stats'],
    queryFn: async () => {
      const response = await api.get('/regulatory-reports/stats');
      return response.data;
    }
  });
  
  // Fetch upcoming deadlines
  const { data: deadlinesData } = useQuery({
    queryKey: ['regulatory-deadlines'],
    queryFn: async () => {
      const response = await api.get('/regulatory-reports/upcoming-deadlines');
      return response.data;
    }
  });
  
  // Generate report mutation
  const generateMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/regulatory-reports/generate', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['regulatory-reports']);
      queryClient.invalidateQueries(['regulatory-stats']);
      setGenerateDialogOpen(false);
      resetGenerateForm();
      toast.success('Report generated successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    }
  });
  
  // Create schedule mutation
  const createScheduleMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/regulatory-reports/schedules', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['regulatory-schedules']);
      setScheduleDialogOpen(false);
      resetScheduleForm();
      toast.success('Schedule created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create schedule');
    }
  });
  
  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ reportId, status, notes }) => {
      const response = await api.put(`/regulatory-reports/${reportId}/status?status=${status}`, { notes });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['regulatory-reports']);
      queryClient.invalidateQueries(['regulatory-stats']);
      toast.success('Status updated');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  });
  
  // Download handler for multiple formats
  const handleDownload = async (reportId, reportNumber, format = 'pdf') => {
    try {
      toast.info(`Generating ${format.toUpperCase()}...`);
      const response = await api.get(`/regulatory-reports/${reportId}/download`, {
        params: { format },
        responseType: 'blob'
      });
      
      // Determine file extension
      const extensions = {
        pdf: 'pdf',
        xml: 'xml',
        eccairs2: '_ECCAIRS2.xml',
        json: 'json'
      };
      const ext = extensions[format] || 'pdf';
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportNumber.replace(/\//g, '-')}${ext.startsWith('_') ? ext : '.' + ext}`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format.toUpperCase()} downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download ${format.toUpperCase()}`);
      console.error('Download error:', error);
    }
  };
  
  // Submit report mutation
  const submitMutation = useMutation({
    mutationFn: async ({ reportId, data }) => {
      const response = await api.post(`/regulatory-reports/${reportId}/submit`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['regulatory-reports']);
      queryClient.invalidateQueries(['regulatory-stats']);
      setSubmitDialogOpen(false);
      setSelectedReport(null);
      toast.success('Submission recorded');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to record submission');
    }
  });
  
  const resetGenerateForm = () => {
    setGenerateForm({
      report_type: '',
      authority: '',
      format: 'pdf',
      date_from: '',
      date_to: '',
      notes: ''
    });
  };
  
  const resetScheduleForm = () => {
    setScheduleForm({
      name: '',
      report_type: '',
      authority: '',
      format: 'pdf',
      frequency: 'monthly',
      submission_method: 'manual',
      recipients: '',
      auto_submit: false,
      review_required: true,
      due_day_of_month: 15
    });
  };
  
  const handleGenerate = () => {
    if (!generateForm.report_type || !generateForm.authority || !generateForm.date_from || !generateForm.date_to) {
      toast.error('Please fill in all required fields');
      return;
    }
    generateMutation.mutate(generateForm);
  };
  
  const handleCreateSchedule = () => {
    if (!scheduleForm.name || !scheduleForm.report_type || !scheduleForm.authority) {
      toast.error('Please fill in all required fields');
      return;
    }
    createScheduleMutation.mutate(scheduleForm);
  };
  
  const reports = reportsData?.reports || [];
  const schedules = schedulesData?.schedules || [];
  const deadlines = deadlinesData?.scheduled_deadlines || [];
  const pendingReports = deadlinesData?.pending_reports || [];
  
  return (
    <div className="space-y-6" data-testid="regulatory-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-2xl font-bold font-barlow", isDark ? "text-white" : "text-gray-900")}>
            Regulatory Report Submission
          </h1>
          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-600")}>
            Generate, manage, and submit regulatory reports to aviation authorities
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setScheduleDialogOpen(true)} className="gap-2">
            <Clock className="h-4 w-4" />
            Schedule Report
          </Button>
          <Button onClick={() => setGenerateDialogOpen(true)} className="gap-2" data-testid="generate-report-btn">
            <Plus className="h-4 w-4" />
            Generate Report
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Total Reports</p>
                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>{stats?.total_reports || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-sky-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Pending Submission</p>
                <p className="text-2xl font-bold text-amber-500">{stats?.pending_submissions || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Submitted (30d)</p>
                <p className="text-2xl font-bold text-emerald-500">{stats?.recent_submissions || 0}</p>
              </div>
              <Send className="h-8 w-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Overdue Schedules</p>
                <p className="text-2xl font-bold text-red-500">{stats?.overdue_schedules || 0}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>Active Schedules</p>
                <p className="text-2xl font-bold text-purple-500">{schedules.filter(s => s.is_active).length}</p>
              </div>
              <Calendar className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="schedules">Scheduled Reports</TabsTrigger>
          <TabsTrigger value="deadlines">Upcoming Deadlines</TabsTrigger>
        </TabsList>
        
        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          {/* Filters */}
          <div className={cn("flex flex-wrap gap-4 p-4 rounded-lg", isDark ? "bg-slate-800/50 border border-slate-700" : "bg-gray-50 border border-gray-200")}>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={cn("w-[180px]", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={authorityFilter} onValueChange={setAuthorityFilter}>
              <SelectTrigger className={cn("w-[180px]", isDark ? "bg-slate-900 border-slate-700" : "bg-white border-gray-300")}>
                <SelectValue placeholder="Authority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Authorities</SelectItem>
                {AUTHORITIES.map(a => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Reports List */}
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
                Generated Reports ({reports.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reportsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-sky-500 border-t-transparent rounded-full"></div>
                </div>
              ) : reports.length === 0 ? (
                <div className={cn("text-center py-12", isDark ? "text-slate-400" : "text-gray-500")}>
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No reports found</p>
                  <Button variant="outline" className="mt-4" onClick={() => setGenerateDialogOpen(true)}>
                    Generate First Report
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {reports.map((report) => {
                    const statusInfo = getStatusInfo(report.status);
                    const StatusIcon = statusInfo.icon;
                    const authority = AUTHORITIES.find(a => a.value === report.authority);
                    const reportType = REPORT_TYPES.find(r => r.value === report.report_type);
                    
                    return (
                      <div 
                        key={report.id}
                        className={cn(
                          "p-4 rounded-lg border flex items-center justify-between",
                          isDark ? "bg-slate-800 border-slate-700 hover:bg-slate-800/70" : "bg-white border-gray-200 hover:bg-gray-50"
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn("p-2 rounded-lg", isDark ? "bg-slate-700" : "bg-gray-100")}>
                            <FileText className="h-6 w-6 text-sky-500" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                                {report.report_number}
                              </span>
                              <Badge className={cn("text-xs border", statusInfo.color)}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {statusInfo.label}
                              </Badge>
                            </div>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>
                              {report.title} • {authority?.label}
                            </p>
                            <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-500")}>
                              Period: {new Date(report.period_from).toLocaleDateString()} - {new Date(report.period_to).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {report.format?.toUpperCase()}
                          </Badge>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setSelectedReport(report); setViewDialogOpen(true); }}>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDownload(report.id, report.report_number, 'pdf')}>
                                <Download className="h-4 w-4 mr-2" />
                                Download PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(report.id, report.report_number, 'xml')}>
                                <Download className="h-4 w-4 mr-2" />
                                Download XML
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(report.id, report.report_number, 'eccairs2')}>
                                <Download className="h-4 w-4 mr-2" />
                                Download ECCAIRS 2
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(report.id, report.report_number, 'json')}>
                                <Download className="h-4 w-4 mr-2" />
                                Download JSON
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {report.status === 'draft' && (
                                <DropdownMenuItem onClick={() => updateStatusMutation.mutate({ reportId: report.id, status: 'pending_review' })}>
                                  <Clock className="h-4 w-4 mr-2" />
                                  Submit for Review
                                </DropdownMenuItem>
                              )}
                              {report.status === 'pending_review' && (
                                <DropdownMenuItem onClick={() => updateStatusMutation.mutate({ reportId: report.id, status: 'approved' })}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                              )}
                              {report.status === 'approved' && (
                                <DropdownMenuItem onClick={() => { setSelectedReport(report); setSubmitDialogOpen(true); }}>
                                  <Send className="h-4 w-4 mr-2" />
                                  Record Submission
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Schedules Tab */}
        <TabsContent value="schedules" className="space-y-4">
          <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
            <CardHeader>
              <CardTitle className={cn(isDark ? "text-white" : "text-gray-900")}>
                Scheduled Reports ({schedules.length})
              </CardTitle>
              <CardDescription>Automated report generation schedules</CardDescription>
            </CardHeader>
            <CardContent>
              {schedulesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-sky-500 border-t-transparent rounded-full"></div>
                </div>
              ) : schedules.length === 0 ? (
                <div className={cn("text-center py-12", isDark ? "text-slate-400" : "text-gray-500")}>
                  <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No scheduled reports</p>
                  <Button variant="outline" className="mt-4" onClick={() => setScheduleDialogOpen(true)}>
                    Create Schedule
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {schedules.map((schedule) => {
                    const authority = AUTHORITIES.find(a => a.value === schedule.authority);
                    const reportType = REPORT_TYPES.find(r => r.value === schedule.report_type);
                    const isOverdue = schedule.next_due && new Date(schedule.next_due) < new Date();
                    
                    return (
                      <div 
                        key={schedule.id}
                        className={cn(
                          "p-4 rounded-lg border flex items-center justify-between",
                          isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"
                        )}
                      >
                        <div className="flex items-center gap-4">
                          <div className={cn("p-2 rounded-lg", schedule.is_active ? (isDark ? "bg-emerald-500/20" : "bg-emerald-100") : (isDark ? "bg-slate-700" : "bg-gray-100"))}>
                            <RefreshCw className={cn("h-6 w-6", schedule.is_active ? "text-emerald-500" : "text-slate-400")} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                                {schedule.name}
                              </span>
                              {!schedule.is_active && (
                                <Badge variant="outline" className="text-xs">Inactive</Badge>
                              )}
                              {isOverdue && (
                                <Badge className="text-xs bg-red-500/20 text-red-400">Overdue</Badge>
                              )}
                            </div>
                            <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>
                              {reportType?.label} • {authority?.label} • {schedule.frequency}
                            </p>
                            <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-500")}>
                              Next due: {schedule.next_due ? new Date(schedule.next_due).toLocaleDateString() : 'Not set'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {schedule.auto_submit && (
                            <Badge variant="outline" className="text-xs">Auto-submit</Badge>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Settings className="h-4 w-4 mr-2" />
                                Edit Schedule
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Generate Now
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-400">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Deadlines Tab */}
        <TabsContent value="deadlines" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Upcoming Scheduled */}
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Calendar className="h-5 w-5 text-sky-500" />
                  Upcoming Scheduled Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                {deadlines.length === 0 ? (
                  <p className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                    No upcoming deadlines
                  </p>
                ) : (
                  <div className="space-y-3">
                    {deadlines.map((schedule, idx) => (
                      <div key={idx} className={cn("p-3 rounded-lg border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                        <div className="flex items-center justify-between">
                          <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{schedule.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {new Date(schedule.next_due).toLocaleDateString()}
                          </Badge>
                        </div>
                        <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-600")}>
                          {AUTHORITIES.find(a => a.value === schedule.authority)?.label}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Pending Reports */}
            <Card className={cn(isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200")}>
              <CardHeader>
                <CardTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
                  <Clock className="h-5 w-5 text-amber-500" />
                  Pending Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingReports.length === 0 ? (
                  <p className={cn("text-center py-8", isDark ? "text-slate-500" : "text-gray-400")}>
                    No pending reports
                  </p>
                ) : (
                  <div className="space-y-3">
                    {pendingReports.slice(0, 5).map((report) => {
                      const statusInfo = getStatusInfo(report.status);
                      return (
                        <div key={report.id} className={cn("p-3 rounded-lg border", isDark ? "bg-slate-800 border-slate-700" : "bg-gray-50 border-gray-200")}>
                          <div className="flex items-center justify-between">
                            <span className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{report.report_number}</span>
                            <Badge className={cn("text-xs border", statusInfo.color)}>{statusInfo.label}</Badge>
                          </div>
                          <p className={cn("text-sm mt-1", isDark ? "text-slate-400" : "text-gray-600")}>
                            {report.title}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Generate Report Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>Generate Regulatory Report</DialogTitle>
            <DialogDescription>Create a new regulatory report for submission</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Report Type *</Label>
              <Select value={generateForm.report_type} onValueChange={(v) => setGenerateForm(prev => ({ ...prev, report_type: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Authority *</Label>
              <Select value={generateForm.authority} onValueChange={(v) => setGenerateForm(prev => ({ ...prev, authority: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue placeholder="Select authority" />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITIES.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label} - {a.fullName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Format</Label>
              <Select value={generateForm.format} onValueChange={(v) => setGenerateForm(prev => ({ ...prev, format: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FORMATS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div></div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Period Start *</Label>
              <Input
                type="date"
                value={generateForm.date_from}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, date_from: e.target.value }))}
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Period End *</Label>
              <Input
                type="date"
                value={generateForm.date_to}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, date_to: e.target.value }))}
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div className="col-span-2">
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Notes</Label>
              <Textarea
                value={generateForm.notes}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes for this report"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleGenerate} disabled={generateMutation.isPending}>
              {generateMutation.isPending ? 'Generating...' : 'Generate Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className={cn("max-w-2xl", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>Schedule Automated Report</DialogTitle>
            <DialogDescription>Set up automatic report generation</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Schedule Name *</Label>
              <Input
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Monthly GACA Safety Report"
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Report Type *</Label>
              <Select value={scheduleForm.report_type} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, report_type: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Authority *</Label>
              <Select value={scheduleForm.authority} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, authority: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue placeholder="Select authority" />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITIES.map(a => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Frequency</Label>
              <Select value={scheduleForm.frequency} onValueChange={(v) => setScheduleForm(prev => ({ ...prev, frequency: v }))}>
                <SelectTrigger className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Due Day of Month</Label>
              <Input
                type="number"
                min="1"
                max="28"
                value={scheduleForm.due_day_of_month}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, due_day_of_month: parseInt(e.target.value) || 15 }))}
                className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateSchedule} disabled={createScheduleMutation.isPending}>
              {createScheduleMutation.isPending ? 'Creating...' : 'Create Schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Submit Dialog */}
      <Dialog open={submitDialogOpen} onOpenChange={setSubmitDialogOpen}>
        <DialogContent className={cn(isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn(isDark ? "text-white" : "text-gray-900")}>Record Submission</DialogTitle>
            <DialogDescription>Record that this report has been submitted to the regulatory authority</DialogDescription>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4 py-4">
              <div className={cn("p-3 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-100")}>
                <p className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>{selectedReport.report_number}</p>
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-600")}>{selectedReport.title}</p>
              </div>
              
              <div>
                <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Reference Number</Label>
                <Input
                  placeholder="Authority reference number"
                  className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                />
              </div>
              
              <div>
                <Label className={cn(isDark ? "text-slate-300" : "text-gray-700")}>Notes</Label>
                <Textarea
                  placeholder="Submission notes"
                  className={cn("mt-1", isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-300")}
                  rows={2}
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => submitMutation.mutate({ 
              reportId: selectedReport?.id, 
              data: { 
                submitted_to: selectedReport?.authority, 
                submission_method: 'manual' 
              }
            })} disabled={submitMutation.isPending}>
              {submitMutation.isPending ? 'Recording...' : 'Record Submission'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Report Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-700" : "bg-white")}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", isDark ? "text-white" : "text-gray-900")}>
              <Eye className="h-5 w-5 text-sky-500" />
              Report Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (() => {
            const statusInfo = getStatusInfo(selectedReport.status);
            const StatusIcon = statusInfo.icon;
            const authority = AUTHORITIES.find(a => a.value === selectedReport.authority);
            const reportType = REPORT_TYPES.find(r => r.value === selectedReport.report_type);
            
            return (
              <div className="space-y-4 py-2">
                {/* Report Header */}
                <div className={cn("p-4 rounded-lg", isDark ? "bg-slate-800" : "bg-gray-100")}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={cn("font-mono font-semibold", isDark ? "text-sky-400" : "text-sky-600")}>
                      {selectedReport.report_number}
                    </span>
                    <Badge className={cn("border", statusInfo.color)}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusInfo.label}
                    </Badge>
                  </div>
                  <h3 className={cn("font-medium", isDark ? "text-white" : "text-gray-900")}>
                    {selectedReport.title}
                  </h3>
                </div>
                
                {/* Report Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Report Type</Label>
                    <p className={cn("mt-1", isDark ? "text-white" : "text-gray-900")}>{reportType?.label || selectedReport.report_type}</p>
                  </div>
                  <div>
                    <Label className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Authority</Label>
                    <p className={cn("mt-1", isDark ? "text-white" : "text-gray-900")}>{authority?.label || selectedReport.authority}</p>
                  </div>
                  <div>
                    <Label className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Format</Label>
                    <p className={cn("mt-1", isDark ? "text-white" : "text-gray-900")}>{selectedReport.format?.toUpperCase() || 'PDF'}</p>
                  </div>
                  <div>
                    <Label className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Period</Label>
                    <p className={cn("mt-1 text-sm", isDark ? "text-white" : "text-gray-900")}>
                      {selectedReport.period_from && new Date(selectedReport.period_from).toLocaleDateString()} - {selectedReport.period_to && new Date(selectedReport.period_to).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                
                {/* Notes */}
                {selectedReport.notes && (
                  <div>
                    <Label className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>Notes</Label>
                    <p className={cn("mt-1 text-sm", isDark ? "text-slate-300" : "text-gray-700")}>{selectedReport.notes}</p>
                  </div>
                )}
                
                {/* Submission Info */}
                {selectedReport.submitted_at && (
                  <div className={cn("p-3 rounded-lg border", isDark ? "bg-emerald-900/20 border-emerald-800" : "bg-emerald-50 border-emerald-200")}>
                    <div className="flex items-center gap-2">
                      <Send className="h-4 w-4 text-emerald-500" />
                      <span className={cn("text-sm font-medium", isDark ? "text-emerald-400" : "text-emerald-700")}>
                        Submitted on {new Date(selectedReport.submitted_at).toLocaleDateString()}
                      </span>
                    </div>
                    {selectedReport.submission_reference && (
                      <p className={cn("text-sm mt-1", isDark ? "text-emerald-300" : "text-emerald-600")}>
                        Reference: {selectedReport.submission_reference}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Created Date */}
                <p className={cn("text-xs", isDark ? "text-slate-500" : "text-gray-400")}>
                  Created: {selectedReport.created_at && new Date(selectedReport.created_at).toLocaleDateString()}
                </p>
              </div>
            );
          })()}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button onClick={() => { handleDownload(selectedReport.id, selectedReport.report_number, 'pdf'); }} className="gap-2">
              <Download className="h-4 w-4" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
